import { useState, useRef, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useAuthStore } from '../../store'
import { FaTruck, FaUserCircle, FaSignOutAlt, FaBars, FaTimes, FaAngleDown } from 'react-icons/fa'
// 导航结构（条目、层级、图标，以及「散货板块已下线」「订单板块已撤销」等历史说明）
// 统一放在 config/navigation.js —— 页脚与面包屑也从那一份派生，本文件只负责渲染。
import { mainNav, userNav } from '../../config/navigation'

export default function Navbar() {
  const location = useLocation()
  const { isAuthenticated, customer, logout } = useAuthStore()
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [hoverMenu, setHoverMenu] = useState(null)
  // 账号菜单里当前展开的分组（目前只有「我的」）。null = 全部收起
  const [expandedUserGroup, setExpandedUserGroup] = useState(null)
  // 移动端主导航：记录【被手动收起】的分组，默认空集 = 全部展开。
  // 存「收起的」而不是「展开的」，这样新增分组时默认行为仍是展开，不用改这里。
  const [collapsedMainGroups, setCollapsedMainGroups] = useState(() => new Set())
  const dropdownRef = useRef(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /*
   * 菜单一关就把展开状态清掉，保证下次打开永远是收起的。
   * 关闭路径有三条（点外部、再点账号按钮、点中某个子项跳走），都汇到这两个开关上，
   * 所以在这里统一重置，比在每条路径里各写一次可靠。
   */
  useEffect(() => {
    if (!isDropdownOpen && !isMobileMenuOpen) setExpandedUserGroup(null)
  }, [isDropdownOpen, isMobileMenuOpen])

  // 菜单关掉就把手动收起的记录清空，保证下次打开一律是展开的
  useEffect(() => {
    if (!isMobileMenuOpen) setCollapsedMainGroups(new Set())
  }, [isMobileMenuOpen])

  const handleLogout = async () => {
    try {
      await logout()
    } catch (error) {
      console.error('Logout failed:', error)
    }
  }

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  // 下拉父项原先没有 active 态：停在「集运板块」任一子页时整条导航一处不亮，看不出所在位置
  const isGroupActive = (link) => link.children?.some((child) => isActive(child.path))

  return (
    <nav className="fixed top-0 left-0 right-0 h-16 bg-gradient-to-r from-primary-800 to-primary-500 shadow-lg z-50">
      <div className="container h-full flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 text-white text-xl font-semibold hover:opacity-90 transition-opacity shrink-0 whitespace-nowrap">
          <FaTruck className="text-2xl" />
          <span>物流集货平台</span>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-1.5 min-w-0">
          {mainNav.map((link) => {
            if (link.auth && !isAuthenticated) return null
            const Icon = link.icon

            if (link.children) {
              return (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => setHoverMenu(link.label)}
                  onMouseLeave={() => setHoverMenu(null)}
                >
                  <button
                    className={`flex items-center gap-1.5 px-3 py-2 text-base text-white/90 rounded-md transition-all duration-200 hover:text-white hover:bg-white/15 shrink-0 whitespace-nowrap ${
                      isGroupActive(link) ? 'text-white bg-white/20' : ''
                    }`}
                  >
                    <Icon className="text-sm" />
                    <span>{link.label}</span>
                    <FaAngleDown
                      className={`text-xs transition-transform duration-200 ${hoverMenu === link.label ? 'rotate-180' : ''}`}
                    />
                  </button>
                  {hoverMenu === link.label && (
                    <div className="absolute top-full left-0 pt-2 w-52 z-50">
                      <div className="bg-white rounded-lg shadow-xl py-2 border border-gray-100">
                        {link.children.map((child) => (
                          (!child.auth || isAuthenticated) && (
                            <Link
                              key={child.label}
                              to={child.path}
                              className="block px-4 py-2.5 text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors"
                              onClick={() => setHoverMenu(null)}
                            >
                              {child.label}
                            </Link>
                          )
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )
            }

            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-1.5 px-3 py-2 text-base text-white/90 rounded-md transition-all duration-200 hover:text-white hover:bg-white/15 shrink-0 whitespace-nowrap ${
                  isActive(link.path) ? 'text-white bg-white/20' : ''
                }`}
              >
                <Icon className="text-sm" />
                <span>{link.label}</span>
              </Link>
            )
          })}
        </div>

        {/* Auth Section */}
        <div className="hidden lg:flex items-center gap-3">
          {isAuthenticated ? (
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 px-3.5 py-1.5 text-white bg-white/15 border border-white/30 rounded-md hover:bg-white/25 hover:border-white/50 transition-all duration-200"
              >
                <FaUserCircle className="text-lg" />
                <span className="max-w-[150px] truncate">{customer?.customer_name || customer?.email || '用户'}</span>
                <span className={`text-xs transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`}>▼</span>
              </button>

              {/* Dropdown Menu */}
              {isDropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-48 bg-white rounded-lg shadow-xl py-2 animate-fadeIn">
                  {userNav.map((item) => {
                    const Icon = item.icon

                    // 可展开分组（「我的」）：标题是按钮不是链接，点它只切展开状态、不跳转
                    if (item.children) {
                      const expanded = expandedUserGroup === item.label
                      return (
                        <div key={item.label}>
                          <button
                            type="button"
                            onClick={() => setExpandedUserGroup(expanded ? null : item.label)}
                            className="flex items-center gap-2.5 w-full px-4 py-2.5 text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-all duration-150"
                          >
                            <Icon className="text-gray-500" />
                            <span className="flex-1 text-left">{item.label}</span>
                            <FaAngleDown className={`text-xs transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                          </button>
                          {expanded && (
                            <div className="bg-gray-50">
                              {item.children.map((child) => {
                                const ChildIcon = child.icon
                                return (
                                  <Link
                                    key={child.path}
                                    to={child.path}
                                    onClick={() => setIsDropdownOpen(false)}
                                    className="flex items-center gap-2.5 pl-10 pr-4 py-2.5 text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-all duration-150"
                                  >
                                    <ChildIcon className="text-gray-500" />
                                    <span>{child.label}</span>
                                  </Link>
                                )
                              })}
                            </div>
                          )}
                        </div>
                      )
                    }

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        onClick={() => setIsDropdownOpen(false)}
                        className="flex items-center gap-2.5 px-4 py-2.5 text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-all duration-150"
                      >
                        <Icon className="text-gray-500" />
                        <span>{item.label}</span>
                      </Link>
                    )
                  })}
                  <div className="h-px bg-gray-200 my-2" />
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2.5 w-full px-4 py-2.5 text-red-600 hover:bg-red-50 transition-all duration-150"
                  >
                    <FaSignOutAlt />
                    <span>退出登录</span>
                  </button>
                </div>
              )}
            </div>
          ) : (
            <>
              {/* state.from：LoginPage 靠它把用户送回原页，见 hooks/useGuest 的 useGoLogin 说明 */}
              <Link
                to="/login"
                state={{ from: location }}
                className="px-5 py-2 text-white border border-white/60 rounded-md hover:bg-white/10 hover:border-white transition-all duration-200"
              >
                登录
              </Link>
              <Link
                to="/signup"
                className="px-5 py-2 text-primary-800 bg-white rounded-md hover:bg-primary-50 transition-all duration-200"
              >
                注册
              </Link>
            </>
          )}
        </div>

        {/* Mobile Menu Button：触控区补到 44x44（iOS 建议值），原先是 38x38 */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="lg:hidden flex items-center justify-center w-11 h-11 shrink-0 text-white border border-white/40 rounded-md"
          aria-label={isMobileMenuOpen ? '关闭菜单' : '打开菜单'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <FaTimes className="text-xl" /> : <FaBars className="text-xl" />}
        </button>
      </div>

      {/*
        菜单必须限高且自身可滚动：它 absolute 挂在 fixed 导航里，页面滚动带不动它。
        实测 375x667 未登录时菜单高 682px，「注册」整个在屏幕外、怎么滑都点不到。
        用 dvh 而非 vh —— 地址栏收放时 vh 不变，会算出比可视区更高的值。
      */}
      {isMobileMenuOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 max-h-[calc(100dvh-4rem)] overflow-y-auto overscroll-contain bg-gradient-to-r from-primary-800 to-primary-500 shadow-lg py-4">
          <div className="container flex flex-col gap-2">
            {mainNav.map((link) => {
              if (link.auth && !isAuthenticated) return null
              const Icon = link.icon

              if (link.children) {
                // 与账号菜单的「我的」同一套折叠交互。原先这里是固定标题、子项常驻展开，
                // 根本合不上。默认仍展开，但现在可以手动收起。
                const expanded = !collapsedMainGroups.has(link.label)
                const toggleGroup = () => setCollapsedMainGroups((prev) => {
                  const next = new Set(prev)
                  if (next.has(link.label)) next.delete(link.label)
                  else next.add(link.label)
                  return next
                })
                return (
                  <div key={link.label}>
                    <button
                      type="button"
                      onClick={toggleGroup}
                      aria-expanded={expanded}
                      className="flex items-center gap-2 w-full px-4 py-3 text-white/85 font-semibold bg-white/5 rounded-md"
                    >
                      <Icon />
                      <span className="flex-1 text-left">{link.label}</span>
                      <FaAngleDown className={`text-xs transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                    </button>
                    {expanded && (
                      <div className="mt-1 ml-4 border-l border-white/20 pl-2">
                        {link.children.map((child) => (
                          (!child.auth || isAuthenticated) && (
                            <Link
                              key={child.label}
                              to={child.path}
                              onClick={() => setIsMobileMenuOpen(false)}
                              className={`block px-4 py-2.5 text-white/70 hover:text-white rounded-md hover:bg-white/10 ${
                                isActive(child.path) ? 'bg-white/15 text-white' : ''
                              }`}
                            >
                              {child.label}
                            </Link>
                          )
                        ))}
                      </div>
                    )}
                  </div>
                )
              }

              return (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-3 text-white/85 rounded-md ${
                    isActive(link.path) ? 'bg-white/20' : ''
                  }`}
                >
                  <Icon />
                  <span>{link.label}</span>
                </Link>
              )
            })}
            <div className="h-px bg-white/20 my-2" />
            {isAuthenticated ? (
              <>
                {userNav.map((item) => {
                  const Icon = item.icon

                  // 与桌面端同一份配置、同一套展开逻辑，只是套用移动端的样式
                  if (item.children) {
                    const expanded = expandedUserGroup === item.label
                    return (
                      <div key={item.label}>
                        <button
                          type="button"
                          onClick={() => setExpandedUserGroup(expanded ? null : item.label)}
                          className="flex items-center gap-2 w-full px-4 py-3 text-white/85 rounded-md"
                        >
                          <Icon />
                          <span className="flex-1 text-left">{item.label}</span>
                          <FaAngleDown className={`text-xs transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`} />
                        </button>
                        {expanded && (
                          <div className="mt-1 ml-4 border-l border-white/20 pl-2">
                            {item.children.map((child) => {
                              const ChildIcon = child.icon
                              return (
                                <Link
                                  key={child.path}
                                  to={child.path}
                                  onClick={() => setIsMobileMenuOpen(false)}
                                  className="flex items-center gap-2 px-4 py-2.5 text-white/70 hover:text-white rounded-md hover:bg-white/10"
                                >
                                  <ChildIcon />
                                  <span>{child.label}</span>
                                </Link>
                              )
                            })}
                          </div>
                        )}
                      </div>
                    )
                  }

                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="flex items-center gap-2 px-4 py-3 text-white/85 rounded-md"
                    >
                      <Icon />
                      <span>{item.label}</span>
                    </Link>
                  )
                })}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 px-4 py-3 text-red-300 rounded-md"
                >
                  <FaSignOutAlt />
                  <span>退出登录</span>
                </button>
              </>
            ) : (
              <div className="flex flex-col gap-2 mt-2">
                <Link
                  to="/login"
                  state={{ from: location }}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full text-center px-4 py-3 text-white border border-white/60 rounded-md"
                >
                  登录
                </Link>
                <Link
                  to="/signup"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="w-full text-center px-4 py-3 text-primary-800 bg-white rounded-md"
                >
                  注册
                </Link>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  )
}
